import { CoreEvent, eventNames, useCoreEvents } from '@carvana/core-events';
import { useEffectOnce } from '@carvana/merch-react-hooks';
import { useRouter } from 'next/navigation';
import { useCallback, useContext, useEffect } from 'react';
import { Consumer } from '~/constants/common/consumer';
import { useIsNative } from '~/context/common/DeviceContext';
import { useTriggerSrpNavigation } from '~/context/common/SrpNavigationContext';
import {
  useConfiguration,
  useEventCorrelationId
} from '~/context/common/StaticContext';
import { useUserLocation } from '~/context/common/UserContext';
import { useTriggerVdpNavigation } from '~/context/common/VdpNavigationContext';
import { useShowComposerTreatment } from '~/context/composer/ComposerContext';
import ModalContext from '~/context/home/ModalContext';
import { ComposerModalName } from '~/context/home/ModalContext/constants';
import useComposerOpenReady from '~/hooks/common/composer/useComposerOpenReady';
import useIsInPriceDropSuppressionExperiment from '~/hooks/common/experiments/useIsInPriceDropSuppressionExperiment';
import {
  SoftPullLocations,
  useOpenSoftPullModal
} from '~/hooks/home/useToggleSoftPullModal';
import buildComposerRedirectUrl, {
  type ComposerSearchInput
} from '~/utilities/composer/buildComposerRedirectUrl';
import buildComposerSuggestionInfo from '~/utilities/composer/buildComposerSuggestionInfo';
import parseComposerFilters from '~/utilities/composer/parseComposerFilters';
import { logError } from '~/utilities/loggers';
import { markSpaNavigationStart } from '~/utilities/navigationTiming';

export default function useComposerEvents() {
  const { setComposerUiModalOpen } = useContext(ModalContext);
  const openSoftPullModal = useOpenSoftPullModal(SoftPullLocations.Composer);
  const eventCorrelationId = useEventCorrelationId();
  const { enableHomeAppRouter } = useConfiguration();
  const { isReady: isComposerReady, whenReady } = useComposerOpenReady();
  const isInPriceDropSuppressionTreatment =
    useIsInPriceDropSuppressionExperiment();
  const isNative = useIsNative();
  const router = useRouter();
  const shouldShowComposer = useShowComposerTreatment();
  const triggerSrpNavigation = useTriggerSrpNavigation();
  const triggerVdpNavigation = useTriggerVdpNavigation();
  const { zip } = useUserLocation();

  // Listeners
  useCoreEvents(
    eventNames.composer.mfe.opened,
    useCallback(
      ({ source }) => {
        if (source !== 'composer') return;

        setComposerUiModalOpen(true);
      },
      [setComposerUiModalOpen]
    )
  );
  useCoreEvents(
    eventNames.composer.mfe.closed,
    useCallback(
      ({ source }) => {
        if (source !== 'composer') return;

        setComposerUiModalOpen(false);
      },
      [setComposerUiModalOpen]
    )
  );
  useCoreEvents(
    eventNames.composer.mfe.isComposerOpened,
    useCallback(
      ({ isOpen, source }) => {
        if (source !== 'composer') return;

        setComposerUiModalOpen(isOpen);
      },
      [setComposerUiModalOpen]
    )
  );
  useCoreEvents(
    eventNames.composer.mfe.openPageModal,
    useCallback(
      ({ modal, source }) => {
        if (source !== 'composer') return;

        switch (modal) {
          case ComposerModalName.SoftPull:
            openSoftPullModal();
            break;
          default:
            logError(new Error('Unknown modal'), { modal });
        }
      },
      [openSoftPullModal]
    )
  );
  useCoreEvents(
    eventNames.composer.mfe.updateSearch,
    useCallback(
      ({ cvnaid, filters, source, suggestionInfo }) => {
        if (source !== 'composer' || isNative) return;

        const composerSuggestionInfo =
          suggestionInfo && buildComposerSuggestionInfo(suggestionInfo);

        let redirectInput: ComposerSearchInput;

        if (cvnaid) {
          redirectInput = { cvnaid, suggestionInfo: composerSuggestionInfo };
        } else {
          const validatedFilters = parseComposerFilters(filters);
          if (!validatedFilters) return;

          redirectInput = {
            filters: validatedFilters,
            suggestionInfo: composerSuggestionInfo
          };
        }

        const redirectUrl = buildComposerRedirectUrl(
          Consumer.HOME,
          redirectInput
        );

        triggerSrpNavigation({
          isInPriceDropSuppressionTreatment,
          shouldShowComposer
        });
        markSpaNavigationStart('navigate');
        router.push(redirectUrl);
      },
      [
        isInPriceDropSuppressionTreatment,
        isNative,
        router,
        shouldShowComposer,
        triggerSrpNavigation
      ]
    )
  );
  useCoreEvents(
    eventNames.composer.mfe.vdp,
    useCallback(
      ({ source, vehicleId }) => {
        if (source !== 'composer' || isNative || !vehicleId) return;

        triggerVdpNavigation();
        markSpaNavigationStart('navigate');
        router.push(`/vehicle/${vehicleId}`);
      },
      [isNative, router, triggerVdpNavigation]
    )
  );

  // Dispatchers
  useEffect(() => {
    if (!isComposerReady) return;

    CoreEvent.emit(eventNames.composer.pageContext, {
      context: {
        capabilities: [
          'handle',
          'isComposerOpen',
          ...(enableHomeAppRouter ? ['isSpaEnabled'] : []),
          'openPageModal',
          'update_search',
          'vdp'
        ],
        merchContextId: eventCorrelationId,
        supportedModals: [ComposerModalName.SoftPull],
        url: window.location.href,
        zip5: zip
      },
      source: 'page',
      surface: 'home'
    });
  }, [enableHomeAppRouter, eventCorrelationId, isComposerReady, zip]);

  useEffectOnce(() => {
    whenReady(() => {
      CoreEvent.emit(eventNames.composer.isComposerOpened, { source: 'page' });
    });
  }, [whenReady]);
}
